import { Link } from "wouter";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ListChecks,
  KeyRound,
  PackageCheck,
  Settings2,
  ShieldCheck,
  LayoutDashboard,
  Sparkles,
  AlertTriangle,
  Ban,
} from "lucide-react";

import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { SALESFORCE_ADMIN_GUIDE } from "@/lib/content";

function GuideStepItem({
  step,
  index,
  sectionId,
}: {
  step: string;
  index: number;
  sectionId: string;
}) {
  return (
    <li
      className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed pl-1"
      data-testid={`${sectionId}-step-${index + 1}`}
    >
      <span className="marker:font-semibold text-indigo-600 dark:text-indigo-400">
        {step}
      </span>
    </li>
  );
}

const SECTION_NAV: ReadonlyArray<{ id: string; label: string }> = [
  { id: "prerequisites", label: "Prerequisites" },
  { id: "rotate-secret", label: "Rotate the embed secret" },
  { id: "deploy-source", label: "Deploy the source" },
  { id: "configure-metadata", label: "Configure settings record" },
  { id: "permission-set", label: "Assign permission set" },
  { id: "add-to-layouts", label: "Add panel to layouts" },
  { id: "what-it-does", label: "What it does" },
  { id: "troubleshooting", label: "Troubleshooting" },
  { id: "out-of-scope", label: "Not in this release" },
  { id: "next-steps", label: "Next steps" },
];

export default function SalesforceAdminGuidePage() {
  const guide = SALESFORCE_ADMIN_GUIDE;

  return (
    <Layout>
      <SEOHead title={guide.seo.title} description={guide.seo.description} />

      {/* Hero */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-20 border-b border-border/50 bg-slate-50 dark:bg-navy/30">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <Link
              href="/resources/docs"
              className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground mb-6 transition-colors"
              data-testid="salesforce-admin-guide-back-to-docs"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to documentation
            </Link>
            <div className="text-xs uppercase tracking-[0.2em] text-indigo font-semibold mb-3">
              {guide.hero.eyebrow}
            </div>
            <h1
              className="text-display mb-5 text-navy dark:text-white"
              data-testid="salesforce-admin-guide-title"
            >
              {guide.hero.title}
            </h1>
            <p className="text-body-lg text-slate-600 dark:text-slate-300 mb-4">
              {guide.hero.subtitle}
            </p>
            <p className="text-sm text-muted-foreground">
              {guide.hero.estimatedTime}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 max-w-6xl mx-auto">
            {/* Sticky on-page nav */}
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-24">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-4">
                  On this page
                </div>
                <nav>
                  <ul className="space-y-2">
                    {SECTION_NAV.map((item) => (
                      <li key={item.id}>
                        <a
                          href={`#${item.id}`}
                          className="block text-sm text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                          data-testid={`salesforce-admin-guide-nav-${item.id}`}
                        >
                          {item.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
                <div className="mt-8 rounded-xl border border-mist bg-white dark:bg-card p-4">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                    {guide.sourcePackage.label}
                  </div>
                  <div
                    className="text-sm font-mono text-navy dark:text-slate-100 mb-2 break-all"
                    data-testid="salesforce-admin-guide-source-location"
                  >
                    {guide.sourcePackage.location}
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {guide.sourcePackage.note}
                  </p>
                </div>
              </div>
            </aside>

            <div className="lg:col-span-9 space-y-16">
              {/* Prerequisites */}
              <section
                id="prerequisites"
                className="scroll-mt-24"
                data-testid="section-prerequisites"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                    <ListChecks className="h-5 w-5" />
                  </div>
                  <h2 className="text-h2 text-navy dark:text-white">
                    {guide.prerequisites.title}
                  </h2>
                </div>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  {guide.prerequisites.body}
                </p>
                <ul className="space-y-3">
                  {guide.prerequisites.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-200"
                    >
                      <CheckCircle2 className="h-4 w-4 text-indigo shrink-0 mt-0.5" />
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Rotate the embed secret */}
              <section
                id="rotate-secret"
                className="scroll-mt-24"
                data-testid="section-rotate-secret"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                    <KeyRound className="h-5 w-5" />
                  </div>
                  <h2 className="text-h2 text-navy dark:text-white">
                    {guide.rotateSecret.title}
                  </h2>
                </div>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  {guide.rotateSecret.intro}
                </p>
                <ol className="space-y-6 list-decimal list-outside ml-5 marker:font-semibold marker:text-indigo-600 dark:marker:text-indigo-400 mb-6">
                  {guide.rotateSecret.steps.map((step, i) => (
                    <GuideStepItem
                      key={step}
                      step={step}
                      index={i}
                      sectionId="rotate-secret"
                    />
                  ))}
                </ol>
                <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-900/30 p-4 flex gap-3">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-900 dark:text-amber-100 leading-relaxed">
                    {guide.rotateSecret.tip}
                  </p>
                </div>
              </section>

              {/* Deploy the source */}
              <section
                id="deploy-source"
                className="scroll-mt-24"
                data-testid="section-deploy-source"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-cyan-50 dark:bg-cyan-900/20 text-cyan flex items-center justify-center">
                    <PackageCheck className="h-5 w-5" />
                  </div>
                  <h2 className="text-h2 text-navy dark:text-white">
                    {guide.deploySource.title}
                  </h2>
                </div>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  {guide.deploySource.intro}
                </p>
                <ol className="space-y-6 list-decimal list-outside ml-5 marker:font-semibold marker:text-indigo-600 dark:marker:text-indigo-400 mb-6">
                  {guide.deploySource.steps.map((step, i) => (
                    <GuideStepItem
                      key={step}
                      step={step}
                      index={i}
                      sectionId="deploy-source"
                    />
                  ))}
                </ol>
                <div className="rounded-2xl border border-mist bg-slate-50 dark:bg-navy/40 p-4 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                  {guide.deploySource.tip}
                </div>
              </section>

              {/* Configure settings record */}
              <section
                id="configure-metadata"
                className="scroll-mt-24"
                data-testid="section-configure-metadata"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                    <Settings2 className="h-5 w-5" />
                  </div>
                  <h2 className="text-h2 text-navy dark:text-white">
                    {guide.configureMetadata.title}
                  </h2>
                </div>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  {guide.configureMetadata.intro}
                </p>
                <ol className="space-y-6 list-decimal list-outside ml-5 marker:font-semibold marker:text-indigo-600 dark:marker:text-indigo-400 mb-6">
                  {guide.configureMetadata.steps.map((step, i) => (
                    <GuideStepItem
                      key={step}
                      step={step}
                      index={i}
                      sectionId="configure-metadata"
                    />
                  ))}
                </ol>
                <div className="rounded-2xl border border-mist bg-slate-50 dark:bg-navy/40 p-4 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                  {guide.configureMetadata.tip}
                </div>
              </section>

              {/* Permission set */}
              <section
                id="permission-set"
                className="scroll-mt-24"
                data-testid="section-permission-set"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <h2 className="text-h2 text-navy dark:text-white">
                    {guide.permissionSet.title}
                  </h2>
                </div>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  {guide.permissionSet.intro}
                </p>
                <ol className="space-y-6 list-decimal list-outside ml-5 marker:font-semibold marker:text-indigo-600 dark:marker:text-indigo-400 mb-6">
                  {guide.permissionSet.steps.map((step, i) => (
                    <GuideStepItem
                      key={step}
                      step={step}
                      index={i}
                      sectionId="permission-set"
                    />
                  ))}
                </ol>
                <div className="rounded-2xl border border-mist bg-slate-50 dark:bg-navy/40 p-6">
                  <h3 className="text-sm font-semibold text-navy dark:text-white mb-3">
                    Good to know
                  </h3>
                  <ul className="space-y-2.5">
                    {guide.permissionSet.callouts.map((callout) => (
                      <li
                        key={callout}
                        className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-200"
                      >
                        <CheckCircle2 className="h-4 w-4 text-indigo shrink-0 mt-0.5" />
                        <span className="leading-relaxed">{callout}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              {/* Add to layouts */}
              <section
                id="add-to-layouts"
                className="scroll-mt-24"
                data-testid="section-add-to-layouts"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-cyan-50 dark:bg-cyan-900/20 text-cyan flex items-center justify-center">
                    <LayoutDashboard className="h-5 w-5" />
                  </div>
                  <h2 className="text-h2 text-navy dark:text-white">
                    {guide.addToLayouts.title}
                  </h2>
                </div>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  {guide.addToLayouts.intro}
                </p>
                <ol className="space-y-6 list-decimal list-outside ml-5 marker:font-semibold marker:text-indigo-600 dark:marker:text-indigo-400 mb-6">
                  {guide.addToLayouts.steps.map((step, i) => (
                    <GuideStepItem
                      key={step}
                      step={step}
                      index={i}
                      sectionId="add-to-layouts"
                    />
                  ))}
                </ol>
                <div className="rounded-2xl border border-mist bg-slate-50 dark:bg-navy/40 p-4 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                  {guide.addToLayouts.tip}
                </div>
              </section>

              {/* What it does */}
              <section
                id="what-it-does"
                className="scroll-mt-24"
                data-testid="section-what-it-does"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <h2 className="text-h2 text-navy dark:text-white">
                    {guide.whatItDoes.title}
                  </h2>
                </div>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  {guide.whatItDoes.body}
                </p>
                <ul className="space-y-3">
                  {guide.whatItDoes.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-200"
                    >
                      <CheckCircle2 className="h-4 w-4 text-indigo shrink-0 mt-0.5" />
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Troubleshooting */}
              <section
                id="troubleshooting"
                className="scroll-mt-24"
                data-testid="section-troubleshooting"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5" />
                  </div>
                  <h2 className="text-h2 text-navy dark:text-white">
                    {guide.troubleshooting.title}
                  </h2>
                </div>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  {guide.troubleshooting.body}
                </p>
                <div className="space-y-4">
                  {guide.troubleshooting.items.map((item) => (
                    <div
                      key={item.symptom}
                      className="rounded-2xl border border-mist bg-white dark:bg-card p-5"
                    >
                      <div className="text-sm font-semibold text-navy dark:text-white mb-2">
                        {item.symptom}
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                        <span className="font-semibold text-indigo-600 dark:text-indigo-400 mr-1.5">
                          Fix:
                        </span>
                        {item.fix}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Out of scope */}
              <section
                id="out-of-scope"
                className="scroll-mt-24"
                data-testid="section-out-of-scope"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 flex items-center justify-center">
                    <Ban className="h-5 w-5" />
                  </div>
                  <h2 className="text-h2 text-navy dark:text-white">
                    {guide.outOfScope.title}
                  </h2>
                </div>
                <p className="text-body text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  {guide.outOfScope.body}
                </p>
                <ul className="space-y-3">
                  {guide.outOfScope.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-200"
                    >
                      <Ban className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Next steps */}
              <section
                id="next-steps"
                className="scroll-mt-24"
                data-testid="section-next-steps"
              >
                <h2 className="text-h2 text-navy dark:text-white mb-6">
                  {guide.nextSteps.title}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {guide.nextSteps.items.map((item) => (
                    <Link
                      key={item.path}
                      href={item.path}
                      className="block rounded-2xl border border-mist bg-white dark:bg-card p-6 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer group"
                      data-testid={`salesforce-admin-guide-next-step-${item.path.replace(/[^a-z0-9]+/gi, "-")}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-navy dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                          {item.label}
                        </span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors" />
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </Link>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
